"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _reactModal = _interopRequireDefault(require("react-modal"));
var _Button = _interopRequireDefault(require("../Button/Button"));
var _useToggler = _interopRequireDefault(require("../../hooks/useToggler"));
var _useTrigger = _interopRequireDefault(require("../../hooks/useTrigger"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

/**
 * The Drawer component provides a way to display information in a panel anchored to the side of the screen.
 *
 * This component is an opinionated wrapper around <a href="https://github.com/reactjs/react-modal">ReactModal</a>.
 */
const Drawer = props => {
  const {
    id: controlledId,
    isDrawerOpen: controlledIsOpen,
    trigger: initialTrigger,
    drawerScopeCSS,
    drawerTitle,
    drawerStickyTitle,
    side,
    onOpen,
    onClose,
    onMinimize,
    disabled,
    children,
    className,
    tabIndex = 0,
    minimizeLabel,
    minmizedIdx,
    shouldReturnFocusAfterClose,
    shouldCloseOnOverlayClick,
    shouldMinimizeOnOverlayClick
  } = props;
  const id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  const [isMinimized, setMinimized] = _react.default.useState(false);
  const {
    isOpen,
    setOpen,
    setClose
  } = (0, _useToggler.default)(false);
  const {
    trigger
  } = (0, _useTrigger.default)(initialTrigger, {
    // ...props,
    id: id,
    tabIndex: tabIndex,
    onClick: event => disabled ? {} : handleOpenDrawer(initialTrigger, event),
    className: (0, _clsx.default)(props.className, 'uk-drawer__trigger')
  });
  _react.default.useEffect(() => {
    if (controlledIsOpen) {
      setOpen();
    } else {
      setClose();
      setMinimized(false);
    }
  }, [controlledIsOpen, setClose, setOpen]);
  const handleOpenDrawer = (trigger, event) => {
    setMinimized(false);
    if (trigger.props && trigger.props.disabled || disabled) {
      return null;
    }
    setOpen();
    if (trigger.props && trigger.props.onClick) {
      trigger.props.onClick(event);
    }
    if (typeof onOpen === 'function') {
      onOpen({
        drawerShow: true
      });
    }
  };
  const handleCloseDrawer = () => {
    if (shouldMinimizeOnOverlayClick) {
      handleMinimizeDrawer();
    } else if (shouldCloseOnOverlayClick) {
      setClose();
      setMinimized(false);
      if (typeof onClose === 'function') {
        onClose({
          drawerShow: false
        });
      }
    }
  };
  const handleMinimizeDrawer = () => {
    if (typeof onMinimize === 'function' && id) {
      onMinimize(id);
    }
    if (!isMinimized) {
      setMinimized(true);
      setClose();
    } else {
      setMinimized(false);
      setOpen();
    }
  };
  const baseClass = (0, _clsx.default)('uk-drawer__inner', side, className);
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, trigger, /*#__PURE__*/_react.default.createElement(_reactModal.default, {
    isOpen: isOpen,
    ariaHideApp: false,
    portalClassName: (0, _clsx.default)('uk-drawer', side, drawerScopeCSS),
    className: {
      base: baseClass,
      afterOpen: 'uk-drawer__inner--after-open',
      beforeClose: 'uk-drawer__inner--before-close'
    },
    overlayClassName: {
      base: 'uk-drawer__overlay',
      afterOpen: 'uk-drawer__overlay--after-open',
      beforeClose: 'uk-drawer__overlay--before-close'
    },
    bodyOpenClassName: "uk-drawer--is-open",
    htmlOpenClassName: "uk-drawer--is-open",
    onRequestClose: handleCloseDrawer,
    shouldReturnFocusAfterClose: shouldReturnFocusAfterClose
    // shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
    ,
    closeTimeoutMS: 720,
    aria: {
      labelledby: 'dialog-label',
      describedby: 'dialog-desc'
    }
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-drawer__wrapper"
  }, drawerTitle && /*#__PURE__*/_react.default.createElement("h5", {
    className: (0, _clsx.default)('uk-drawer__title', drawerStickyTitle && 'uk-drawer__title--sticky'),
    id: "dialog-label"
  }, drawerTitle), /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-drawer__body",
    id: "dialog-desc"
  }, typeof children === 'function' ? children({
    isOpen,
    setClose,
    handleCloseDrawer,
    handleMinimizeDrawer
  }) : children))), minimizeLabel && isMinimized && /*#__PURE__*/_react.default.createElement(_Button.default, {
    className: "uk-drawer__minimized",
    color: "warning",
    size: "small",
    label: minimizeLabel,
    onClick: () => handleMinimizeDrawer(),
    style: minmizedIdx >= 0 ? {
      right: "calc(2rem + " + 0.5 * minmizedIdx + "rem + " + minmizedIdx * 160 + "px)"
    } : undefined
  }));
};
Drawer.propTypes = {
  /**
   * Prefix for the Drawer id
   */
  id: _propTypes.default.string,
  /**
   * Show or hide the Drawer (controlled)
   */
  isDrawerOpen: _propTypes.default.bool,
  /**
   * Trigger of the Drawer
   * If the trigger isn't a React element, it will be wrapped within a <span/>
   */
  trigger: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]).isRequired,
  /**
   * As the Drawer is displayed in a ReactPortal, it may need to have its CSS scoped
   */
  drawerScopeCSS: _propTypes.default.string,
  /**
   * Title of the Drawer
   */
  drawerTitle: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Title of the Drawer
   */
  drawerStickyTitle: _propTypes.default.bool,
  /**
   * Side from which the Drawer will be anchored
   */
  side: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Callback function onOpen
   */
  onOpen: _propTypes.default.func,
  /**
   * Callback function onClose
   */
  onClose: _propTypes.default.func,
  /**
   * Callback function onMinimize
   */
  onMinimize: _propTypes.default.func,
  /**
   * Set the number as tabIndex
   */
  tabIndex: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number]),
  /**
   * Title minimize label
   */
  minimizeLabel: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Possibility to close by clicking outside the overlay
   */
  shouldCloseOnOverlayClick: _propTypes.default.bool,
  /**
   * Show or hide minimize overlay
   */
  shouldMinimizeOnOverlayClick: _propTypes.default.bool
};
Drawer.defaultProps = {
  isDrawerOpen: false,
  trigger: '',
  side: 'right',
  shouldCloseOnOverlayClick: true,
  shouldMinimizeOnOverlayClick: false,
  drawerStickyTitle: false
};
var _default = exports.default = Drawer;